// Right rail — alerts, saved searches, activity

import React from "react";
import { Badge, ComingSoon, Toggle } from "./atoms.jsx";
import { ACTIVITY, ALERT_HISTORY, CHANNELS, SAVED_SEARCHES } from "./data.jsx";
import { I } from "./icons.jsx";
import "./alerts.css";


const ChannelIcon = ({ ch }) => {
  const icons = {
    discord: <I.hash size={10}/>, discord_webhook: <I.hash size={10}/>,
    email: <I.mail size={10}/>, web_push: <I.bell size={10}/>,
  };
  return <span className="ico" title={ch}>{icons[ch] || <I.bell size={10}/>}</span>;
};

const AlertCard = ({ alert, onToggle, onOpen }) => (
  <div className={`alert-card ${alert.enabled ? "" : "disabled"}`} onClick={onOpen}>
    <div className="a-top">
      <div className="a-name">
        <span className={`prio-dot ${alert.tier === "offer" ? "high" : alert.tier === "deal" ? "med" : "low"}`}/>
        {alert.name}
      </div>
      <Toggle on={alert.enabled} onChange={()=>onToggle(alert.id)} variant="accent" ariaLabel={`${alert.name} alert`}/>
    </div>
    <div className="a-crit">{alert.criteria}</div>
    <div className="a-foot">
      <div className="a-ch">
        {alert.channels.map(ch => <ChannelIcon key={ch} ch={ch}/>)}
        <span style={{fontSize:10, color:"var(--muted-2)", marginLeft:2, fontFamily:"var(--font-mono)"}}>v{alert.version}</span>
      </div>
      <div className="mono" style={{fontSize:10.5}}>
        {alert.matches24h} · 24h · {alert.lastTriggered}
      </div>
    </div>
  </div>
);

const HistoryRow = ({ h }) => (
  <div className="hist-row">
    <div className="h-top">
      <div className="h-rule">{h.rule} <span style={{color:"var(--muted-2)", fontFamily:"var(--font-mono)", fontSize:10}}>v{h.ruleVersion}</span></div>
      <span className={`dstatus ${h.status}`}><span className="dd"/>{h.status}</span>
    </div>
    <div className="h-item">{h.item} · <span style={{color: h.ratio < 1 ? "var(--accent-deep)" : "var(--ink-2)", fontWeight:600, fontFamily:"var(--font-mono)"}}>{h.ratio.toFixed(2)}×</span></div>
    <div className="h-meta">
      <ChannelIcon ch={h.channel}/>
      <span>{h.channel.replace("_"," ")}</span>
      <span>· {h.sentAt}</span>
      {h.attempts > 1 && <span>· {h.attempts} attempts</span>}
      {h.nextRetry && <span style={{color:"var(--accent-deep)"}}>· retry {h.nextRetry}</span>}
      {h.error && <span style={{color:"var(--hot)"}}>· {h.error}</span>}
    </div>
  </div>
);

const ChannelRow = ({ c }) => {
  const isDiscord = c.type === "discord_webhook";
  const soon = c.status === "coming_soon";
  return (
    <div className={`chan-row ${isDiscord ? "discord" : ""} ${soon ? "soon" : ""}`}>
      <span className="ch-ic">{isDiscord ? <I.hash size={15}/> : c.type === "email" ? <I.mail size={15}/> : <I.bell size={15}/>}</span>
      <div className="ch-body">
        <div className="ch-name">{c.label}</div>
        <div className="ch-sub">{c.type.replace("_"," ")}{c.fingerprint ? ` · ${c.fingerprint}` : ""}</div>
      </div>
      {soon
        ? <Badge tone="outline">soon</Badge>
        : <div style={{display:"flex", gap:6, alignItems:"center"}}>
            <button className="btn xs ghost" title="Send test"><I.bolt size={11}/></button>
            <Toggle on={c.enabled} onChange={()=>{}} variant="accent" ariaLabel={`${c.label} channel`}/>
          </div>}
    </div>
  );
};

const RailGate = ({ onSignIn }) => (
  <div className="rail-gate">
    <div className="g-hero">
      <h3>Never miss a steal</h3>
      <p>Browsing is free. Sign in to unlock the power-user loop — set a rule once and get pinged the instant a matching deal lands.</p>
      <button className="btn accent" style={{width:"100%", justifyContent:"center"}} onClick={onSignIn}><I.bolt size={14}/> Sign in with email</button>
      <div style={{fontSize:10.5, color:"var(--muted)", marginTop:8, textAlign:"center"}}>Magic link — no password.</div>
    </div>
    <div className="g-list">
      <div className="g-item"><span className="gi"><I.bell size={12}/></span><div><b>Alert rules</b> — thresholds on ratio, weight, karat, confidence. Delivered to Discord.</div></div>
      <div className="g-item"><span className="gi"><I.pulse size={12}/></span><div><b>Live stream</b> — new deals appear the moment they're analyzed.</div></div>
      <div className="g-item"><span className="gi"><I.star size={12}/></span><div><b>Save</b> items and searches to a personal watchlist.</div></div>
      <div className="g-item"><span className="gi"><I.spark size={12}/></span><div><b>Feedback loop</b> — report real karat &amp; weight to sharpen estimates.</div></div>
    </div>
  </div>
);

const MatchSparkline = () => {
  // fake sparkline: hourly matches last 24h
  const data = [3,2,4,1,2,6,8,7,5,9,12,14,11,8,6,7,10,13,18,15,11,9,7,12];
  const max = Math.max(...data);
  const w = 288, h = 42, bw = w / data.length;
  return (
    <div className="match-spark">
      <div style={{display:"flex", alignItems:"baseline", justifyContent:"space-between"}}>
        <div>
          <div className="micro">Matches · 24h</div>
          <div className="mono" style={{fontSize:16, color:"var(--ink)", marginTop:2, fontWeight:500}}>184 <span style={{color:"var(--live)", fontSize:12, fontWeight:500}}>▲ 22%</span></div>
        </div>
        <div className="mono" style={{fontSize:10.5, color:"var(--muted)"}}>per hr</div>
      </div>
      <svg className="spark-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        {data.map((v, i) => {
          const bh = (v / max) * (h - 4);
          return <rect key={i} x={i*bw + 1} y={h - bh} width={bw - 2} height={bh} fill="var(--accent)" opacity={i >= data.length - 4 ? 1 : 0.45} rx="1"/>;
        })}
      </svg>
    </div>
  );
};

const LegacyRightRail = ({ alerts, onToggle, signedIn, onCreateRule, onComingSoon }) => {
  return (
    <aside className="right-rail">
      <div className="right-tabs">
        <button className="active"><I.bell size={12}/> Alerts <span className="num">soon</span></button>
      </div>
      <div className="right-body">
        <ComingSoon
          variant="panel"
          feature="Account tools"
          detail="Alert rules, notification channels, alert history, and saved searches are gated until accounts ship."
          onAction={() => onComingSoon?.("Account tools", "Alert rules, notification channels, alert history, and saved searches are gated until accounts ship.")}
          actionLabel="Coming soon"
        />
      </div>
    </aside>
  );

  if (!signedIn) {
    return (
      <aside className="right-rail">
        <div className="right-tabs">
          <button className="active"><I.bolt size={12}/> Get alerts</button>
        </div>
        <div className="right-body"><RailGate onSignIn={onSignIn}/></div>
      </aside>
    );
  }

  return (
    <aside className="right-rail">
      <div className="right-tabs">
        <button className={tab === "alerts" ? "active" : ""} onClick={() => setTab("alerts")}>
          <I.bell size={12}/> Rules <span className="num">{alerts.filter(a => a.enabled).length}</span>
        </button>
        <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>
          <I.pulse size={12}/> History
        </button>
        <button className={tab === "channels" ? "active" : ""} onClick={() => setTab("channels")}>
          <I.hash size={12}/> Channels
        </button>
        <button className={tab === "activity" ? "active" : ""} onClick={() => setTab("activity")}>
          <I.spark size={12}/> Live
        </button>
      </div>
      <div className="right-body">
        {tab === "alerts" && (
          <>
            <MatchSparkline/>
            <div className="create-alert">
              <h4>New alert rule</h4>
              <div className="t">Set thresholds once; KaratScan pings your Discord the moment a new listing matches.</div>
              <div style={{display:"flex", gap:"6px"}}>
                <button className="btn accent sm" style={{flex:1, justifyContent:"center"}} onClick={onCreateRule}>
                  <I.plus size={12}/> Build rule
                </button>
                <button className="btn sm" title="From current filters" onClick={onCreateRule}><I.sliders size={12}/></button>
              </div>
            </div>
            <div style={{padding:"8px 14px 4px"}} className="micro">Your rules</div>
            {alerts.map(a => <AlertCard key={a.id} alert={a} onToggle={onToggle} onOpen={onCreateRule}/>)}
            <div style={{padding:"10px 14px"}} className="micro">Saved searches</div>
            {SAVED_SEARCHES.map(s => (
              <div key={s.id} className="saved-row">
                <span className="stripe" style={{background: s.color}}/>
                <span><span className="sn">{s.name}</span><span className="sc" style={{display:"block"}}>{s.crit}</span></span>
                <span className="sc">{s.count} new</span>
              </div>
            ))}
          </>
        )}
        {tab === "history" && (
          <>
            <div style={{padding:"14px 14px 8px"}} className="micro">Alert deliveries</div>
            {ALERT_HISTORY.map(h => <HistoryRow key={h.id} h={h}/>)}
          </>
        )}
        {tab === "channels" && (
          <>
            <div style={{padding:"14px 14px 8px"}} className="micro">Notification channels</div>
            {CHANNELS.map(c => <ChannelRow key={c.id} c={c}/>)}
            <div style={{padding:"16px 14px"}}>
              <button className="btn accent" style={{width:"100%", justifyContent:"center"}}><I.plus size={13}/> Add Discord webhook</button>
              <div style={{fontSize:11, color:"var(--muted)", marginTop:8, lineHeight:1.4, textAlign:"center"}}>Email &amp; web-push delivery are coming soon. We only ever store a secret fingerprint, never the webhook URL.</div>
            </div>
          </>
        )}
        {tab === "activity" && (
          <>
            <div style={{padding:"14px 14px 8px", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
              <span className="micro">Live stream · SSE</span>
              <span className="dstatus sent"><span className="dd"/>connected</span>
            </div>
            {ACTIVITY.map((a, i) => (
              <div key={i} className="act-row">
                <div className="t">{a.t}</div>
                <div className="msg"><span className={`pin ${a.kind}`}/>{a.text}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </aside>
  );
};

const RightSummary = ({ signedIn, enabledCount }) => (
  <div className="right-summary" aria-label="Alerts summary">
    <div className="right-stat">
      <div className="rs-v">{signedIn ? enabledCount : "0"}</div>
      <div className="rs-k">Active rules</div>
    </div>
    <div className="right-stat">
      <div className="rs-v">{signedIn ? ALERT_HISTORY.length : "Soon"}</div>
      <div className="rs-k">Delivery history</div>
    </div>
  </div>
);

const AlertsDrawer = ({ alerts, onToggle, signedIn, onCreateRule, onComingSoon, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const [tab, setTab] = React.useState("alerts");
  const enabledCount = alerts.filter(a => a.enabled).length;
  const panelId = "alerts-drawer-panel";

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const openAccountTools = () => onComingSoon?.(
    "Account tools",
    "Alert rules, notification channels, alert history, and saved searches are gated until accounts ship."
  );

  return (
    <aside className={`alerts-shell ${open ? "open" : "collapsed"}`} data-testid="alerts-drawer" aria-label="Alerts drawer">
      <button
        className="alerts-dock"
        data-testid="alerts-drawer-toggle"
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? "Collapse alerts drawer" : "Expand alerts drawer"}
        title={open ? "Collapse alerts" : "Expand alerts"}
        onClick={() => setOpen(v => !v)}
      >
        <span
          className="alerts-dock-btn"
          aria-hidden="true"
        >
          <I.bell size={15}/>
          <span className="alerts-dock-count">{signedIn ? enabledCount : "soon"}</span>
        </span>
        <span className="alerts-dock-label" aria-hidden="true">Alerts</span>
      </button>

      {open && (
        <section className="right-rail" id={panelId} aria-label="Alerts panel">
          <div className="right-head">
            <div>
              <h2>Alerts</h2>
              <div className="rh-sub">
                Rules, channels, and delivery history stay tucked away until you need them.
              </div>
            </div>
            <button className="btn icon sm ghost" type="button" onClick={() => setOpen(false)} aria-label="Collapse alerts drawer" title="Collapse alerts">
              <I.chevR size={14}/>
            </button>
          </div>
          <RightSummary signedIn={signedIn} enabledCount={enabledCount}/>

          {!signedIn ? (
            <div className="right-body">
              <ComingSoon
                variant="panel"
                feature="Account tools"
                detail="Alert rules, notification channels, alert history, and saved searches are gated until accounts ship."
                onAction={openAccountTools}
                actionLabel="Coming soon"
              />
            </div>
          ) : (
            <>
              <div className="right-tabs">
                <button className={tab === "alerts" ? "active" : ""} onClick={() => setTab("alerts")}>
                  <I.bell size={12}/> Rules <span className="num">{enabledCount}</span>
                </button>
                <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>
                  <I.pulse size={12}/> History
                </button>
                <button className={tab === "channels" ? "active" : ""} onClick={() => setTab("channels")}>
                  <I.hash size={12}/> Channels
                </button>
                <button className={tab === "activity" ? "active" : ""} onClick={() => setTab("activity")}>
                  <I.spark size={12}/> Live
                </button>
              </div>
              <div className="right-body">
                {tab === "alerts" && (
                  <>
                    <MatchSparkline/>
                    <div className="create-alert">
                      <h4>New alert rule</h4>
                      <div className="t">Set thresholds once; KaratScan pings your Discord the moment a new listing matches.</div>
                      <div style={{display:"flex", gap:"6px"}}>
                        <button className="btn accent sm" style={{flex:1, justifyContent:"center"}} onClick={onCreateRule}>
                          <I.plus size={12}/> Build rule
                        </button>
                        <button className="btn sm" title="From current filters" onClick={onCreateRule}><I.sliders size={12}/></button>
                      </div>
                    </div>
                    <div style={{padding:"8px 14px 4px"}} className="micro">Your rules</div>
                    {alerts.map(a => <AlertCard key={a.id} alert={a} onToggle={onToggle} onOpen={onCreateRule}/>)}
                    <div style={{padding:"10px 14px"}} className="micro">Saved searches</div>
                    {SAVED_SEARCHES.map(s => (
                      <div key={s.id} className="saved-row">
                        <span className="stripe" style={{background: s.color}}/>
                        <span><span className="sn">{s.name}</span><span className="sc" style={{display:"block"}}>{s.crit}</span></span>
                        <span className="sc">{s.count} new</span>
                      </div>
                    ))}
                  </>
                )}
                {tab === "history" && (
                  <>
                    <div style={{padding:"14px 14px 8px"}} className="micro">Alert deliveries</div>
                    {ALERT_HISTORY.map(h => <HistoryRow key={h.id} h={h}/>)}
                  </>
                )}
                {tab === "channels" && (
                  <>
                    <div style={{padding:"14px 14px 8px"}} className="micro">Notification channels</div>
                    {CHANNELS.map(c => <ChannelRow key={c.id} c={c}/>)}
                    <div style={{padding:"16px 14px"}}>
                      <button className="btn accent" style={{width:"100%", justifyContent:"center"}}><I.plus size={13}/> Add Discord webhook</button>
                      <div style={{fontSize:11, color:"var(--muted)", marginTop:8, lineHeight:1.4, textAlign:"center"}}>Email &amp; web-push delivery are coming soon. We only ever store a secret fingerprint, never the webhook URL.</div>
                    </div>
                  </>
                )}
                {tab === "activity" && (
                  <>
                    <div style={{padding:"14px 14px 8px", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                      <span className="micro">Live stream Â· SSE</span>
                      <span className="dstatus sent"><span className="dd"/>connected</span>
                    </div>
                    {ACTIVITY.map((a, i) => (
                      <div key={i} className="act-row">
                        <div className="t">{a.t}</div>
                        <div className="msg"><span className={`pin ${a.kind}`}/>{a.text}</div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </section>
      )}
    </aside>
  );
};

export { AlertsDrawer, AlertsDrawer as RightRail };
